import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import useFetchingMyTickets from "./useFetchingMyTickets";
import { useAuth } from "@/components/auth/AuthProvider";
import { useEffect, useState } from "react";

/**
 * Fetches the number of unique connections (other attendees) for a given user across specified events.
 *
 * @param userId - The ID of the user for whom to fetch connections.
 * @param eventIds - An array of event IDs to check for connections.
 * @returns A promise that resolves to the number of unique connections.
 */

const getMyEventConnections = async (userId: string, eventIds: string[]) => {
  // Get unique connections (other attendees)
  const { data: connections } = await supabase
    .from("tickets")
    .select(
      `
        user_id,
        events!inner (id)
      `
    )
    .in(
      "events.id",
      eventIds,
    )
    .neq("user_id", userId);

  const uniqueConnections = new Set(connections?.map((c) => c.user_id) || []);
  return uniqueConnections.size;
}

// This hook is used to fetch the user's event connections
const useFetchingMyEventConnections = () => {
  const { userId } = useAuth()
  const { data: tickets } = useFetchingMyTickets();
  const [connections, setConnections] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId || !tickets) return; // skip calculate if no user or tickets
    setIsLoading(true);
    getMyEventConnections(userId, tickets.map((t) => t.events.id))
      .then(setConnections)
      .finally(() => setIsLoading(false));
  }, [tickets, userId]);
  
  return { connections, isLoading };
};

export default useFetchingMyEventConnections;
